import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from 'reselect';
import { Card, CardHeader, CardBody, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalHeader, ModalBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import getChartColorsArray from '../../Components/Common/ChartsDynamicColor';
import Preloader from '../../Components/Common/Preloader';
import { getStatistics, setStatisticsChartData, toggleStatisticsModal } from '../../slices/analytics/reducer';
import DetailCard from './DetailCard';
import { getStatisticChart } from '../../slices/analytics/thunk';


function StatisticsChart() {
  const dispatch = useDispatch();
  const [chartData, setChartData] = useState(null);
  const [type, setType] = useState('day');

  const selectStates = createSelector(state => state, state => ({
    statisticsChartLoader: state.Analytics.statisticsChartLoader,
    statisticsChartData: state.Analytics.statisticsChartData,
    analyticsCampaigns: state.Analytics.analyticsCampaigns,
  }))
  const { statisticsChartLoader, statisticsChartData, analyticsCampaigns } = useSelector(selectStates);

  function openStatistics() {
    dispatch(getStatisticChart(analyticsCampaigns));
  }

  useEffect(() => {
    if (statisticsChartData) {
      if (Object.keys(statisticsChartData).length > 0) {
        showDay();
      } else {
        setChartData([]);
      }
      // setChartData(statisticsChartData);
    } else {
      setChartData(null);
    }
  }, [statisticsChartData]);

  function handleChartData(statisticChartData) {
    const chartArr = [];
    for (const [key, value] of Object.entries(statisticChartData)) {
      chartArr.push({ date: key, value: value });
    }

    const sortedArr = chartArr.sort((a, b) => {
      const dateA = new Date(a.date.replace(/-(\d)(?=-|T|$)/g, '-0$1'));
      const dateB = new Date(b.date.replace(/-(\d)(?=-|T|$)/g, '-0$1'));
      return dateA - dateB;
    })

    const updatedArr = sortedArr.map(item => {
      return {
        ...item,
        date: formatDate(item.date)
      }
    })

    setChartData(updatedArr);
  }

  function formatDate(dateStr) {
    const correctedDateStr = dateStr.replace(/-(\d)(?=-|T|$)/g, '-0$1');
    const date = new Date(correctedDateStr);
    const hasTime = correctedDateStr.includes('T');

    const options = hasTime ? {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    } : { month: 'short', day: '2-digit' };

    return date.toLocaleString('en-US', options).replace(',', '');
  }

  function showHour() {
    if (statisticsChartData) {
      if (Object.keys(statisticsChartData).length > 0) {
        setType('hour');
        const filteredChartObj = filterChartData(statisticsChartData, 'hour');
        handleChartData(filteredChartObj);  
      }
      
    }
  }

  function showDay() {
    if (statisticsChartData) {
      if (Object.keys(statisticsChartData).length > 0) {
        setType('day')
        const filteredChartObj = filterChartData(statisticsChartData, 'day');
        handleChartData(filteredChartObj)  
      }
    }
  }

  function filterChartData(statisticChartData, type) {
    let data = {};
    const keys = Object.keys(statisticChartData);
    let i = 0;
    do {
      const item = statisticChartData[keys[i]];
      const dayKey = type === 'day' ? keys[i].split('T')[0] : keys[i];
      if (data[dayKey]) {
        data[dayKey].impression += item.impression;
        data[dayKey].click += item.click;
      } else {
        data[dayKey] = {};
        data[dayKey].impression = item.impression;
        data[dayKey].click = item.click;
      }
      i++;
    } while (i < keys.length);
    return data;
  }

  return (
    <React.Fragment>
      {statisticsChartLoader ?
        <Card className='card-height-100 align-items-center justify-content-center'>
          <Preloader isComponentLoader={true} width={150} height={150} />
        </Card>
        : chartData ?
          <Card className="card-height-100">
            <CardHeader className="align-items-center d-flex">
              <p className="mb-0 flex-grow-1">Overview</p>
              <div className="align-items-center d-flex">
                <div className="flex-shrink-0">
                  <button type="button" className="btn btn-light text-muted  btn-sm" onClick={showHour}>
                    HOUR
                  </button>
                </div>
                <div className="flex-shrink-0 ms-2">
                  <button type="button" className="btn btn-soft-info btn-sm" onClick={showDay}>
                    DAY
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardBody className="px-0">
              <div id="revenue-expenses-charts" dir="ltr">
                {chartData &&
                  <BalanceOverviewCharts data={chartData} dataColors='["#0ab39c", "#405189"]' />
                }
              </div>
            </CardBody>
          </Card> :
          <DetailCard color='primary'
            textColor="white"
            avatar="ri-line-chart-line"
            header="Daily Clicks & Impressions Overview"
            text="View the number of clicks and impressions your ads receive each day to analyze performance trends and optimize your campaigns over time."
            style={{minHeight: '300px'}}
            onClick={openStatistics} />
      }
    </React.Fragment>
  )
}

export default StatisticsChart;

function BalanceOverviewCharts({ dataColors, data, series }) {
  var revenueExpensesChartsColors = getChartColorsArray(dataColors);

  var options = {
    chart: {
      height: 380,
      type: 'line',
      stacked: false,
      zoom: {
        enabled: false
      },
      toolbar: {
        show: false,
      }
    },
    colors: revenueExpensesChartsColors,
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: [3, 4],
      curve: 'straight',
      // dashArray: [0, 8]
    },
    series: [{ name: 'Impressions', data: data.map(item => item.value.impression) }, { name: 'Clicks', data: data.map(item => item.value.click) }],
    title: {
      text: 'Page Statistics',
      align: 'left',
      style: {
        fontWeight: 500,
      },
    },
    markers: {
      size: 0,
      hover: {
        sizeOffset: 6
      }
    },
    xaxis: {
      categories: data.map(item => item.date),
    },
    tooltip: {
      y: [{
        title: {
          formatter: function (val) {
            return val + ":"
          }
        }
      }, {
        title: {
          formatter: function (val) {
            return val + ":"
          }
        }
      }]
    },
    grid: {
      borderColor: '#f1f1f1',
    }
  }

  return (
    <ReactApexChart dir="ltr"
      options={options}
      series={options.series}
      type="line"
      height="380"
      className="apex-charts"
    />
  )
}